/**
 * SpiritLoader Component
 *
 * Main loading animation component for Spirit of Shabbat.
 * Combines Torah scroll, Shabbat candles, Hebrew text, and particles
 * into a cohesive canvas-based loading experience.
 *
 * Includes multiple failsafe mechanisms to prevent stuck loading:
 * 1. AnimationEngine has a 30s failsafe timeout
 * 2. React-level failsafe timeout as backup
 * 3. Error handling for canvas initialization failures
 */

import { useRef, useEffect, useCallback } from 'react';
import { AnimationEngine } from './canvas/AnimationEngine';
import { TorahScrollAnimation } from './canvas/TorahScrollAnimation';
import { ShabbatCandlesAnimation } from './canvas/ShabbatCandlesAnimation';
import { HebrewTextAnimation } from './canvas/HebrewTextAnimation';
import { ParticleSystemAnimation } from './canvas/ParticleSystem';
import { useLoadingState, useHideHtmlPreloader } from './hooks/useLoadingState';
import { LOADER_COLORS, type AnimationPhase } from './types';

// React-level failsafe timeout (slightly longer than AnimationEngine's 30s)
const REACT_FAILSAFE_TIMEOUT_MS = 35000;

interface SpiritLoaderProps {
  /** Whether the app is ready to display */
  isReady?: boolean;

  /** Called when the loading animation is complete */
  onComplete?: () => void;

  /** Minimum time to display the loader in ms */
  minDisplayTime?: number;

  /** Error state */
  hasError?: boolean;
  errorMessage?: string;
}

export function SpiritLoader({
  isReady = false,
  onComplete,
  minDisplayTime = 1800,
  hasError = false,
  errorMessage,
}: SpiritLoaderProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<AnimationEngine | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hasCompletedRef = useRef(false);
  const failsafeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track isReady via ref so the rAF callback in the init effect always has
  // the latest value. This fixes a race condition where isReady becomes true
  // before the engine is created (common in Safari with cached auth state).
  const isReadyRef = useRef(isReady);
  isReadyRef.current = isReady;

  const { phase, setPhase } = useLoadingState({
    minDisplayTime,
    isReady,
    hasError,
    errorMessage,
  });

  // Stable callback for completion that prevents double-calling
  const handleComplete = useCallback(() => {
    if (hasCompletedRef.current) return;
    hasCompletedRef.current = true;

    // Clear failsafe timeout
    if (failsafeTimeoutRef.current) {
      clearTimeout(failsafeTimeoutRef.current);
      failsafeTimeoutRef.current = null;
    }

    onComplete?.();
  }, [onComplete]);

  // Hide the static HTML preloader when React loader takes over
  // Run unconditionally - don't wait for canvas ref
  useEffect(() => {
    const htmlPreloader = document.getElementById('spirit-preloader');
    if (htmlPreloader) {
      // Immediately hide the HTML preloader since React has taken over
      // Use CSS classes for cleaner DOM manipulation
      htmlPreloader.classList.add('opacity-0', 'transition-opacity', 'duration-100');
      const timeoutId = setTimeout(() => {
        htmlPreloader.classList.add('hidden');
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, []);

  // React-level failsafe timeout - backup in case AnimationEngine fails
  useEffect(() => {
    failsafeTimeoutRef.current = setTimeout(() => {
      if (!hasCompletedRef.current) {
        console.warn(
          '[SpiritLoader] React-level failsafe timeout triggered - forcing completion after',
          REACT_FAILSAFE_TIMEOUT_MS,
          'ms'
        );
        setPhase('complete');
        handleComplete();
      }
    }, REACT_FAILSAFE_TIMEOUT_MS);

    return () => {
      if (failsafeTimeoutRef.current) {
        clearTimeout(failsafeTimeoutRef.current);
        failsafeTimeoutRef.current = null;
      }
    };
  }, [setPhase, handleComplete]);

  // Initialize animation engine
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      // If canvas is not available, trigger failsafe after a short delay
      const noCanvasTimeout = setTimeout(() => {
        if (!hasCompletedRef.current) {
          console.warn('[SpiritLoader] Canvas not available, forcing completion');
          setPhase('complete');
          handleComplete();
        }
      }, 5000);
      return () => clearTimeout(noCanvasTimeout);
    }

    // Create animation engine
    const engine = new AnimationEngine({
      introMinDuration: minDisplayTime,
      respectReducedMotion: true,
    });

    // Register animation modules (order matters for render layering)
    engine.registerModule(new ParticleSystemAnimation());
    engine.registerModule(new ShabbatCandlesAnimation());
    engine.registerModule(new TorahScrollAnimation());
    engine.registerModule(new HebrewTextAnimation());

    // Set up callbacks
    engine.setOnPhaseChange((newPhase: AnimationPhase) => {
      setPhase(newPhase);
    });

    engine.setOnComplete(() => {
      handleComplete();
    });

    // Initialize and start - use rAF to ensure layout is computed on mobile
    const rafId = requestAnimationFrame(() => {
      try {
        engine.init(canvas);
        engine.start();
        engineRef.current = engine;

        // If isReady was already true before the engine was created (race condition),
        // set externalReady now. This is critical for Safari where cached auth state
        // can resolve before the first rAF fires, causing the isReady effect to miss.
        if (isReadyRef.current) {
          engine.setExternalReady(true);
        }
      } catch (error) {
        // If engine initialization fails, force completion
        console.error('[SpiritLoader] Engine initialization failed:', error);
        setPhase('complete');
        handleComplete();
      }
    });

    return () => {
      cancelAnimationFrame(rafId);
      engineRef.current?.destroy();
      engineRef.current = null;
    };
  }, [minDisplayTime, setPhase, handleComplete]);

  // Update engine when isReady changes
  useEffect(() => {
    if (engineRef.current && isReady) {
      engineRef.current.setExternalReady(true);
    }
  }, [isReady]);

  // Keep the engine in sync with hook phase changes.
  // If the useLoadingState hook independently triggers exit (because isReady
  // was true before the engine caught up), tell the engine to exit too.
  // This prevents the hook and engine phases from becoming desynchronized,
  // which would cause the engine to never fire onComplete.
  useEffect(() => {
    if (phase === 'exit' && engineRef.current) {
      const enginePhase = engineRef.current.getPhase();
      if (enginePhase === 'loop' || enginePhase === 'intro') {
        engineRef.current.setExternalReady(true);
        engineRef.current.triggerExit();
      }
    }
  }, [phase]);

  // Hide the HTML preloader when phase is complete
  useHideHtmlPreloader(phase === 'complete');

  // Note: We don't return null here even when complete
  // The parent component (App.tsx) controls when to remove us from DOM
  // This ensures the fade animation completes before unmounting

  // Calculate opacity for smooth exit - fade during exit phase
  // Match the 500ms exit duration from AnimationEngine
  const containerOpacity = phase === 'exit' || phase === 'complete' ? 0 : 1;

  const loopOrCompleteValue = phase === 'loop' ? 75 : 100;
  const ariaValueNow = phase === 'intro' ? 25 : loopOrCompleteValue;

  return (
    <div
      ref={containerRef}
      className={`
        fixed inset-0 z-50
        flex items-center justify-center
        ${phase === 'exit' || phase === 'complete' ? 'pointer-events-none' : ''}
      `}
      style={{
        background: `linear-gradient(135deg, ${LOADER_COLORS.primaryWhisper} 0%, ${LOADER_COLORS.primaryGhost} 50%, ${LOADER_COLORS.primaryFaint} 100%)`,
        opacity: containerOpacity,
        transition: 'opacity 500ms ease-out',
      }}
      role="progressbar"
      aria-label="Loading Spirit of Shabbat"
      aria-busy={phase !== 'complete'}
      aria-valuenow={ariaValueNow}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <canvas
        ref={canvasRef}
        className="w-full h-full"
        style={{
          width: '100vw',
          height: '100vh',
          maxWidth: '100vw',
          maxHeight: '100vh',
        }}
      />

      {/* Screen reader announcement */}
      <div className="sr-only" role="status" aria-live="polite">
        {phase === 'intro' && 'Loading Spirit of Shabbat...'}
        {phase === 'loop' && 'Almost ready...'}
        {phase === 'exit' && 'Loading complete'}
      </div>
    </div>
  );
}

export default SpiritLoader;
